import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../components/ui/use-toast';
import { Download, RefreshCcw, Filter, Search, X } from 'lucide-react';

const ActivityLogs = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    action: '',
    user_id: '',
    start_date: '',
    end_date: ''
  });
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Check authentication and permissions
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!user) {
      return;
    }



    // Only admin users can access activity logs
    if (user.role !== 'admin' && !user.isSuperuser) {
      navigate('/home');
      return;
    }

    fetchActivityLogs();
    fetchUsers();
  }, [isAuthenticated, user, navigate]);

  const fetchActivityLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const params = new URLSearchParams();
      if (filters.action) params.append('action', filters.action);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await api.get(`/api/activity-logs/?${params.toString()}`);
      setActivityLogs(response.data);
    } catch (err) {
      console.error('Error fetching activity logs:', err);
      if (err.response?.status === 403) {
        setError('Access denied. You do not have permission to view activity logs.');
        toast({ title: 'Error', description: 'Access denied. You do not have permission to view activity logs.', variant: 'destructive' });
      } else {
        setError('Failed to fetch activity logs. Please try again.');
        toast({ title: 'Error', description: 'Failed to fetch activity logs. Please try again.', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/');
      // Handle different response formats
      if (Array.isArray(response.data)) {
        setUsers(response.data);
      } else if (response.data && Array.isArray(response.data.results)) {
        setUsers(response.data.results);
      } else {
        console.warn('Unexpected users response format:', response.data);
        setUsers([]);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsers([]); // Set empty array on error
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    setPage(1); // Reset to first page when applying filters
    fetchActivityLogs();
  };

  const handleClearFilters = () => {
    setFilters({
      action: '',
      user_id: '',
      start_date: '',
      end_date: ''
    });
    setPage(1); // Reset to first page when clearing filters
    fetchActivityLogs();
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setPage(1); // Reset to first page when changing page size
  };

  const handleRefresh = () => {
    fetchActivityLogs();
  };

  const getActionColor = (action) => {
    const actionColors = {
      'user_login': 'bg-green-100 text-green-800',
      'user_logout': 'bg-blue-100 text-blue-800',
      'inactive_logout': 'bg-orange-100 text-orange-800',
      'vehicle_create': 'bg-blue-100 text-blue-800',
      'vehicle_update': 'bg-yellow-100 text-yellow-800',
      'vehicle_delete': 'bg-red-100 text-red-800',
      'document_upload': 'bg-green-100 text-green-800',
      'document_download': 'bg-blue-100 text-blue-800',
      'document_delete': 'bg-red-100 text-red-800',
      'deployment_create': 'bg-blue-100 text-blue-800',
      'deployment_update': 'bg-yellow-100 text-yellow-800',
      'deployment_delete': 'bg-red-100 text-red-800',
      'project_create': 'bg-blue-100 text-blue-800',
      'project_update': 'bg-yellow-100 text-yellow-800',
      'user_create': 'bg-green-100 text-green-800',
      'user_update': 'bg-yellow-100 text-yellow-800',
      'user_delete': 'bg-red-100 text-red-800',
      'role_change': 'bg-purple-100 text-purple-800',
      'permission_change': 'bg-purple-100 text-purple-800',
      'system_backup': 'bg-green-100 text-green-800',
      'system_restore': 'bg-yellow-100 text-yellow-800'
    };
    return actionColors[action] || 'bg-gray-100 text-gray-800';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Pagination calculations
  const totalPages = Math.ceil(activityLogs.length / pageSize);
  const start = (page - 1) * pageSize;
  const currentLogs = activityLogs.slice(start, start + pageSize);

  const exportToCSV = () => {
    const csvContent = [
      ['Timestamp', 'User', 'Action', 'Details', 'IP Address', 'Related Object'],
      ...activityLogs.map(log => [
        formatTimestamp(log.timestamp),
        log.user_username || 'System',
        log.action_display,
        log.details,
        log.ip_address || 'N/A',
        log.related_object || 'N/A'
      ])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast({ title: 'Success', description: 'Activity logs exported to CSV' });
  };

  if (!isAuthenticated) {
    return null;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (user.accessLevel !== 'admin' && user.accessLevel !== 'delete' && !user.isSuperuser) {
    return (
      <div className="min-h-screen bg-gray-100 flex justify-center items-center">
        <div className="text-red-600 p-4 bg-white rounded shadow">Access denied. You do not have permission to view activity logs.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-semibold">Activity Logs</h2>
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportToCSV}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={handleRefresh}>
              <RefreshCcw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5" />
              <h3 className="text-lg font-medium">Filters</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Select value={filters.action} onValueChange={(value) => handleFilterChange('action', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Actions</SelectItem>
                  <SelectItem value="user_login">User Login</SelectItem>
                  <SelectItem value="user_logout">User Logout</SelectItem>
                  <SelectItem value="inactive_logout">Inactive Logout</SelectItem>
                  <SelectItem value="vehicle_create">Vehicle Created</SelectItem>
                  <SelectItem value="vehicle_update">Vehicle Updated</SelectItem>
                  <SelectItem value="vehicle_delete">Vehicle Deleted</SelectItem>
                  <SelectItem value="document_upload">Document Uploaded</SelectItem>
                  <SelectItem value="document_download">Document Downloaded</SelectItem>
                  <SelectItem value="document_delete">Document Deleted</SelectItem>
                  <SelectItem value="deployment_create">Deployment Created</SelectItem>
                  <SelectItem value="deployment_update">Deployment Updated</SelectItem>
                  <SelectItem value="deployment_delete">Deployment Deleted</SelectItem>
                  <SelectItem value="project_create">Project Created</SelectItem>
                  <SelectItem value="project_update">Project Updated</SelectItem>
                  <SelectItem value="user_create">User Created</SelectItem>
                  <SelectItem value="user_update">User Updated</SelectItem>
                  <SelectItem value="user_delete">User Deleted</SelectItem>
                  <SelectItem value="role_change">Role Changed</SelectItem>
                  <SelectItem value="permission_change">Permission Changed</SelectItem>
                  <SelectItem value="system_backup">System Backup</SelectItem>
                  <SelectItem value="system_restore">System Restore</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.user_id} onValueChange={(value) => handleFilterChange('user_id', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Users</SelectItem>
                  {users && Array.isArray(users) && users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.username} ({user.access_level})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                type="date"
                placeholder="Start Date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
              />

              <Input
                type="date"
                placeholder="End Date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
              />

              <div className="flex gap-2">
                <Button onClick={handleApplyFilters} className="flex-1">
                  <Search className="w-4 h-4 mr-2" />
                  Apply
                </Button>
                <Button variant="outline" onClick={handleClearFilters} className="flex-1">
                  <X className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              </div>
            </div>
            
            {/* Logs per Page Selector */}
            <div className="mt-4 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Logs per Page:</label>
                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="border rounded px-2 py-1 text-sm"
                >
                  {[25, 50, 100, 200].map(size => (
                    <option key={size} value={size}>{size} per page</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Activity Logs Table */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-medium mb-4">
              Activity Logs ({activityLogs.length} entries) - Page {page} of {totalPages}
            </h3>
            
            {loading ? (
              <div className="flex justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-200 table-fixed">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-4 py-2 text-left font-medium w-32">Timestamp</th>
                      <th className="border border-gray-200 px-4 py-2 text-left font-medium w-24">User</th>
                      <th className="border border-gray-200 px-4 py-2 text-left font-medium w-32">Action</th>
                      <th className="border border-gray-200 px-4 py-2 text-left font-medium w-80">Details</th>
                      <th className="border border-gray-200 px-4 py-2 text-left font-medium w-32">IP Address</th>
                      <th className="border border-gray-200 px-4 py-2 text-left font-medium w-40">Related Object</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentLogs.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="border border-gray-200 px-4 py-8 text-center text-gray-500">
                          No activity logs found
                        </td>
                      </tr>
                    ) : (
                      currentLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="border border-gray-200 px-4 py-2">
                            <div className="text-sm text-gray-600">
                              {log.timestamp_formatted}
                            </div>
                            <div className="text-xs text-gray-400">
                              {formatTimestamp(log.timestamp)}
                            </div>
                          </td>
                          <td className="border border-gray-200 px-4 py-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              log.user_username ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {log.user_username || 'System'}
                            </span>
                          </td>
                          <td className="border border-gray-200 px-4 py-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                              {log.action_display}
                            </span>
                          </td>
                          <td className="border border-gray-200 px-4 py-2">
                            <div className="text-sm max-w-md break-words" title={log.details}>
                              {log.details}
                            </div>
                          </td>
                          <td className="border border-gray-200 px-4 py-2">
                            <span className="text-sm text-gray-600">
                              {log.ip_address || 'N/A'}
                            </span>
                          </td>
                          <td className="border border-gray-200 px-4 py-2">
                            <span className="text-sm text-gray-600">
                              {log.related_object || 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination Controls */}
            {!loading && activityLogs.length > 0 && (
              <div className="flex items-center justify-between mt-4 text-sm">
                <span className="text-muted-foreground">
                  Showing {start + 1} to {Math.min(start + pageSize, activityLogs.length)} of {activityLogs.length} entries
                </span>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ActivityLogs;
